// src/components/common/compactListSx.ts

import type { SxProps, Theme } from '@mui/material';

/**
 * Compact list styles — applied via `sx` prop on React Admin's `<List>`.
 *
 * Overrides RA's internal padding, margins and alignment to produce
 * a tight, modern toolbar row (filters left, actions right) with
 * minimal vertical footprint.
 *
 * IMPORTANT — React Admin 5 CSS class gotcha:
 * In RA5, `ListToolbar` is `styled('div', { name: 'RaListToolbar' })`.
 * However, emotion does NOT reliably generate `.RaListToolbar-root` as a
 * real DOM class — only the emotion hash class is applied. The class
 * `.RaList-actions` IS reliable because it is passed explicitly via the
 * `className` prop. Therefore, ALL toolbar selectors here target
 * `.RaList-actions` exclusively.
 *
 * For embedded lists (inside SubMenu tabs), override toolbar padding:
 *   <List sx={[compactListSx, {
 *     '& .RaList-actions': { paddingTop: '2px !important', paddingBottom: '2px !important' }
 *   }]} />
 *
 * Usage:
 *   import { compactListSx } from '@/components/common/compactListSx';
 *   <List sx={compactListSx} ...>
 */
export const compactListSx: SxProps<Theme> = {
  // ── Main container ─────────────────────────────────────────────
  '& .RaList-main': {
    paddingTop: '0 !important',
    paddingLeft: '12px !important',
    paddingRight: '12px !important',
    paddingBottom: '0 !important',
  },

  // ── Toolbar row (filters + actions) ────────────────────────────
  '& .RaList-actions': {
    minHeight: '0 !important',
    paddingTop: '16px !important',
    paddingBottom: '14px !important',
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    marginBottom: '0 !important',
    borderBottom: 'none !important',
    // Hide the <span /> spacer that RA injects
    '& > span': { display: 'none' },
  },

  // ── FilterForm (RA's <form> wrapper) ───────────────────────────
  '& .RaList-actions form': {
    margin: '0 !important',
    padding: '0 !important',
    minHeight: '0 !important',
    flex: '1 1 auto',
  },

  // ── FilterForm inner Box (has RA defaults: mb=1, align=flex-end)
  '& .RaList-actions form > .MuiBox-root': {
    alignItems: 'center !important',
    marginBottom: '0 !important',
    marginTop: '0 !important',
    minHeight: '0 !important',
  },
};

/**
 * Contained-list layout: the toolbar keeps its natural height, the datagrid
 * scrolls inside the card (both axes, sticky header) and the pagination bar
 * stays visible at the bottom. Apply AFTER compactListSx:
 *   <List sx={[compactListSx, containedListSx]} ...>
 */
export const containedListSx: SxProps<Theme> = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',

  // Toolbar: natural height, top-aligned, no negative offsets from RA.
  '& .RaList-actions': {
    height: 'auto !important',
    minHeight: 'auto !important',
    alignItems: 'flex-start !important',
    flexWrap: 'wrap',
    rowGap: '8px',
    flex: '0 0 auto',
  },
  '& .RaList-actions form': {
    marginTop: '0 !important',
    minHeight: 'auto !important',
  },
  '& .RaFilterForm-root': {
    marginTop: '0 !important',
    minHeight: 'auto !important',
  },

  // Main column fills the card; the datagrid area is the flexible part.
  '& .RaList-main': {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  '& .RaList-content': {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  '& .RaDatagrid-root': {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  '& .RaDatagrid-tableWrapper': {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  '& .RaDatagrid-tableWrapper thead th': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: (theme) => theme.palette.background.paper,
  },

  // Compact rows.
  '& .RaDatagrid-rowCell': {
    paddingTop: '6px',
    paddingBottom: '6px',
  },

  // Pagination pinned under the table.
  '& .MuiTablePagination-root': {
    flex: '0 0 auto',
    overflow: 'visible',
    minHeight: 'auto',
  },
};
